import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import useConversationContext from '../hooks/useConversationContext';
import PriceChart from './charts/PriceChart';
import ChartModal from './ChartModal';
import { chartRequestHandler, onServerWaking } from '../api';
import useTitle from '../hooks/useTitle';
import { fmtMoney, fmtPct, signClass } from '../utils/format';
import { normalizeMarkdown } from '../utils/markdown';
import './ChatPage.css';

// Ask the analyst. Answer first: the assistant's opening paragraph is set in
// the serif and the evidence table follows. Replies stream in; if streaming is
// unavailable the plain endpoint answers in one piece.

const STARTERS = [
  'How is my portfolio doing today?',
  'What is my biggest risk right now?',
  'When are my next earnings dates?',
  'Which holding is dragging my returns?',
];

const FOLLOW_UPS = [
  'What would change this?',
  'Put that in numbers',
  'How does that compare with the S&P 500?',
];

function toDate(v) {
  if (!v) return null;
  if (v.toDate) return v.toDate();
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

function groupConversations(list) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
  const groups = { Today: [], Yesterday: [], Earlier: [] };
  list.forEach((c) => {
    const d = toDate(c.updatedAt) || toDate(c.createdAt);
    if (d && d >= today) groups.Today.push(c);
    else if (d && d >= yesterday) groups.Yesterday.push(c);
    else groups.Earlier.push(c);
  });
  return Object.entries(groups).filter(([, items]) => items.length);
}

const Icon = {
  list: <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true"><path d="M3 5h12M3 9h12M3 13h8" /></svg>,
  plus: <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true"><path d="M7 2v10M2 7h10" /></svg>,
  trash: <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M2.5 4.5h11M6 4.5v-1a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v1M4 4.5l.6 8a1 1 0 0 0 1 .9h4.8a1 1 0 0 0 1-.9l.6-8" /></svg>,
  pen: <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M11 2.5l2.5 2.5L6 12.5H3.5V10z" /></svg>,
  send: <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 8h10M9 4l4 4-4 4" /></svg>,
};

function Answer({ content, chartConfig, chartSuggestions, onChart, streaming, holdingsCount }) {
  return (
    <article className={`cp-answer ${streaming ? 'cp-answer--streaming' : ''}`}>
      <div className="cp-answer-body">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{normalizeMarkdown(content)}</ReactMarkdown>
        {streaming && !content && <span className="cp-thinking dd-mono">Reading your holdings…</span>}
      </div>
      {chartConfig?.series && (
        <figure className="cp-chart">
          <figcaption className="cp-chart-head">
            <span className="dd-mono">{chartConfig.symbol} · {String(chartConfig.period || '').toUpperCase()}</span>
            <span className="dd-serif dd-num">{fmtMoney(chartConfig.summary?.end_price, chartConfig.currency || 'USD', chartConfig.summary?.end_price >= 1000 ? 0 : 2)}</span>
            <span className={`dd-mono ${signClass(chartConfig.summary?.change_percent)}`}>{fmtPct(chartConfig.summary?.change_percent)}</span>
            <button type="button" className="cp-textbtn" onClick={() => onChart(chartConfig.symbol)}>Open</button>
          </figcaption>
          <PriceChart series={chartConfig.series} benchmark={chartConfig.benchmark} currency={chartConfig.currency || 'USD'} height={200} compact showBenchmark ariaLabel={`${chartConfig.symbol} price chart`} />
        </figure>
      )}
      {!streaming && chartSuggestions?.length > 0 && (
        <div className="cp-chips">
          {chartSuggestions.map((s) => (
            <button key={s.symbol} type="button" className="cp-chip" onClick={() => onChart(s.symbol)}>{s.symbol} chart</button>
          ))}
        </div>
      )}
      {!streaming && (
        <div className="cp-sources dd-mono dd-muted">
          Live prices via Finnhub{holdingsCount ? ` · your ${holdingsCount} holdings` : ''} · analysis, not advice
        </div>
      )}
    </article>
  );
}

export default function ChatPage({ user }) {
  const navigate = useNavigate();
  const location = useLocation();
  useTitle('Ask the analyst');
  const {
    conversationHistory, userProfile, loading, usageInfo, isMessageLoading, messageError, clearMessageError,
    sendMessage, sendMessageStream, conversations, activeConversationId,
    createConversation, switchConversation, deleteConversation, renameConversation,
  } = useConversationContext(user);

  const [input, setInput] = useState('');
  const [draft, setDraft] = useState(null);
  const [listOpen, setListOpen] = useState(false);
  const [chart, setChart] = useState(null);
  const [chartSymbolInput, setChartSymbolInput] = useState('');
  const [showChartInput, setShowChartInput] = useState(false);
  const [renaming, setRenaming] = useState(null);
  const [renameText, setRenameText] = useState('');
  const [waking, setWaking] = useState(false);
  const endRef = useRef(null);
  const draftText = useRef('');

  const isPremium = Boolean(user?.isPremium);
  const holdingsCount = userProfile?.holdings?.length || 0;
  const dailyLimit = usageInfo?.daily_limit ?? (isPremium ? 100 : 10);
  const used = usageInfo?.total_today ?? 0;
  const atLimit = usageInfo?.remaining === 0;

  useEffect(() => onServerWaking(setWaking), []);

  // Another page can hand over a question, e.g. the chart modal's "Ask the analyst about NVDA".
  useEffect(() => {
    const prefill = location.state?.prefill;
    if (prefill) {
      setInput(prefill);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.state, location.pathname, navigate]);

  useEffect(() => {
    endRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'end' });
  }, [conversationHistory.length, draft]);

  const groups = useMemo(() => groupConversations(conversations || []), [conversations]);

  const send = useCallback(async (text) => {
    const msg = String(text ?? input).trim();
    if (!msg || isMessageLoading) return;
    setInput('');
    clearMessageError();
    draftText.current = '';
    setDraft({ user: msg, assistant: '' });
    try {
      await sendMessageStream(msg, (delta) => {
        draftText.current += delta;
        setDraft((d) => (d ? { ...d, assistant: d.assistant + delta } : d));
      });
    } catch (err) {
      if (!draftText.current) {
        try {
          await sendMessage(msg);
        } catch (err2) {
          // the hook has already surfaced the error
        }
      }
    } finally {
      setDraft(null);
    }
  }, [input, isMessageLoading, clearMessageError, sendMessageStream, sendMessage]);

  const openChart = (symbol) => {
    const sym = String(symbol || '').toUpperCase();
    const held = (userProfile?.holdings || []).find((h) => String(h.ticker || '').toUpperCase() === sym);
    setChart({ symbol: sym, period: '6mo', position: held ? { quantity: held.quantity, avgPrice: held.avgPrice } : null });
  };

  const startRename = (c) => { setRenaming(c.id); setRenameText(c.title || ''); };
  const commitRename = async () => {
    if (renaming && renameText.trim()) await renameConversation(renaming, renameText.trim());
    setRenaming(null);
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const showStarters = !loading && conversationHistory.length === 0 && !draft;
  const lastAssistant = conversationHistory.length > 0 && conversationHistory[conversationHistory.length - 1].role === 'assistant';

  return (
    <div className={`cp ${listOpen ? 'cp--list-open' : ''}`}>
      <aside className="cp-list" aria-label="Conversations">
        <button type="button" className="dd-btn-ghost cp-new" onClick={() => { createConversation(); setListOpen(false); }}>
          {Icon.plus} New question
        </button>
        <div className="cp-list-scroll">
          {groups.map(([label, items]) => (
            <div key={label} className="cp-group">
              <div className="dd-eyebrow">{label}</div>
              {items.map((c) => (
                <div key={c.id} className={`cp-conv ${c.id === activeConversationId ? 'on' : ''}`}>
                  {renaming === c.id ? (
                    <input
                      className="cp-rename"
                      value={renameText}
                      autoFocus
                      onChange={(e) => setRenameText(e.target.value)}
                      onBlur={commitRename}
                      onKeyDown={(e) => { if (e.key === 'Enter') commitRename(); if (e.key === 'Escape') setRenaming(null); }}
                    />
                  ) : (
                    <button type="button" className="cp-conv-title" onClick={() => { switchConversation(c.id); setListOpen(false); }} title={c.title}>
                      {c.title || 'New question'}
                    </button>
                  )}
                  <span className="cp-conv-actions">
                    <button type="button" className="cp-icon" aria-label="Rename" onClick={() => startRename(c)}>{Icon.pen}</button>
                    <button type="button" className="cp-icon" aria-label="Delete" onClick={() => { if (window.confirm('Delete this conversation?')) deleteConversation(c.id); }}>{Icon.trash}</button>
                  </span>
                </div>
              ))}
            </div>
          ))}
          {!groups.length && <p className="dd-muted cp-empty-list">Your questions will be kept here.</p>}
        </div>
      </aside>

      <main className="cp-main">
        <div className="cp-top">
          <div className="cp-top-left">
            <button type="button" className="cp-icon cp-list-toggle" aria-label="Conversations" onClick={() => setListOpen((o) => !o)}>{Icon.list}</button>
            <h1 className="dd-serif">Ask the analyst</h1>
            {isPremium && <span className="cp-pro dd-mono">Pro</span>}
          </div>
          <div className="cp-top-right dd-mono dd-muted">
            {holdingsCount ? `Answers use your ${holdingsCount} holdings and live prices` : 'Add holdings for answers about your portfolio'}
            {!holdingsCount && <button type="button" className="cp-textbtn" onClick={() => navigate('/settings')}>Add holdings</button>}
          </div>
        </div>

        <div className="cp-scroll">
          <div className="cp-thread">
            {loading && <p className="dd-muted cp-loading dd-mono">Loading your conversation…</p>}

            {showStarters && (
              <div className="cp-starters">
                <p className="dd-serif">What do you want to know?</p>
                <div className="cp-chips">
                  {STARTERS.map((s) => <button key={s} type="button" className="cp-chip" onClick={() => send(s)}>{s}</button>)}
                </div>
              </div>
            )}

            {conversationHistory.map((m, i) => (
              m.role === 'user' ? (
                <div key={`u-${i}`} className="cp-user">{m.content}</div>
              ) : (
                <Answer
                  key={`a-${i}`}
                  content={m.content}
                  chartConfig={m.chartConfig}
                  chartSuggestions={m.chartSuggestions}
                  onChart={openChart}
                  holdingsCount={holdingsCount}
                />
              )
            ))}

            {draft && (
              <>
                <div className="cp-user">{draft.user}</div>
                <Answer content={draft.assistant} streaming holdingsCount={holdingsCount} />
                {waking && !draft.assistant && <p className="cp-waking dd-mono dd-muted">The server is waking up on free hosting. This first answer can take a minute.</p>}
              </>
            )}

            {lastAssistant && !draft && !isMessageLoading && (
              <div className="cp-chips cp-followups">
                {FOLLOW_UPS.map((f) => <button key={f} type="button" className="cp-chip" onClick={() => send(f)}>{f}</button>)}
              </div>
            )}

            {messageError && (
              <div className="cp-error" role="alert">
                <span>{String(messageError)}</span>
                {atLimit && !isPremium && <a href="/premium">See Pro</a>}
                <button type="button" className="cp-textbtn" onClick={clearMessageError}>Dismiss</button>
              </div>
            )}
            <div ref={endRef} />
          </div>
        </div>

        <div className="cp-composer">
          <form className="cp-form" onSubmit={(e) => { e.preventDefault(); send(); }}>
            <label htmlFor="cp-input" className="cp-sr">Ask about your holdings</label>
            <textarea
              id="cp-input"
              rows={1}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              placeholder={holdingsCount ? `Ask about your ${holdingsCount} holdings or any market question` : 'Ask any market question'}
              disabled={isMessageLoading}
            />
            <button type="button" className="dd-btn-ghost cp-chart-btn" onClick={() => setShowChartInput((v) => !v)} aria-expanded={showChartInput}>Chart</button>
            <button type="submit" className="dd-btn cp-send" disabled={isMessageLoading || !input.trim()} aria-label="Send">
              {isMessageLoading ? 'Thinking…' : 'Ask'} {!isMessageLoading && Icon.send}
            </button>
          </form>
          {showChartInput && (
            <form className="cp-chart-form" onSubmit={(e) => { e.preventDefault(); if (chartSymbolInput.trim()) { openChart(chartSymbolInput.trim()); setShowChartInput(false); setChartSymbolInput(''); } }}>
              <input className="cp-chart-input dd-mono" value={chartSymbolInput} onChange={(e) => setChartSymbolInput(e.target.value)} placeholder="Ticker, e.g. NVDA" autoFocus />
              <button type="submit" className="dd-btn-ghost">Draw</button>
            </form>
          )}
          <div className="cp-meta dd-mono dd-muted">
            <span>{used} of {dailyLimit} questions today{atLimit ? ' · limit reached' : ''}</span>
            <span>Figures can be wrong. Verify before acting.</span>
          </div>
        </div>
      </main>

      <ChartModal
        isOpen={Boolean(chart)}
        onClose={() => setChart(null)}
        symbol={chart?.symbol}
        period={chart?.period || '6mo'}
        position={chart?.position || null}
        onChartRequest={chartRequestHandler}
      />
    </div>
  );
}
